import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Infer } from "spacetimedb";

import { tableQueryOptions } from "@/web/lib/tanstack";
import { tables } from "@tycho/web-bindings";
import type CampaignBrowseNominationsRow from "@tycho/web-bindings/campaign_browse_nominations_table";
import type CampaignBrowseShareableCardsRow from "@tycho/web-bindings/campaign_browse_shareable_cards_table";
import type MyBusinessCardInterestsRow from "@tycho/web-bindings/my_business_card_interests_table";
import type MyNominationInterestsRow from "@tycho/web-bindings/my_nomination_interests_table";

type CampaignBrowseNomination = Infer<typeof CampaignBrowseNominationsRow>;
type CampaignBrowseShareableCard = Infer<typeof CampaignBrowseShareableCardsRow>;
type MyBusinessCardInterest = Infer<typeof MyBusinessCardInterestsRow>;
type MyNominationInterest = Infer<typeof MyNominationInterestsRow>;

export type NominatePlaceMatch =
  | {
      kind: "nomination";
      nominationId: string;
      googlePlaceId: string;
      name: string;
      location?: string;
      nominatedByName?: string;
      totalInterested: number;
      totalInterestDollars: number;
      heroCdnPath?: string;
      userInterestDollars?: number;
      userReason?: string;
      userFirstName?: string;
      userLastName?: string;
      userConsentToDisplay?: boolean;
    }
  | {
      kind: "onPlatform";
      cardId: string;
      googlePlaceId: string;
      name: string;
      location?: string;
      totalInterested: number;
      totalInterestDollars: number;
      heroCdnPath?: string;
      userInterest?: {
        interestDollarAmount: number;
        firstName: string | null;
        lastName: string | null;
        supportiveMessage: string | null;
        consentToDisplay: boolean;
      };
    }
  | {
      kind: "onPlatformViewOnly";
      cardId: string;
      name: string;
    };

function toNumber(value: number | bigint | undefined): number {
  if (value === undefined) return 0;
  return typeof value === "bigint" ? Number(value) : value;
}

export function formatNominateLocation(
  address: { city?: string; state?: string } | null | undefined,
): string | undefined {
  if (!address) return undefined;
  const parts = [address.city, address.state].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(", ") : undefined;
}

function nominationToMatch(
  nomination: CampaignBrowseNomination,
  myInterest: MyNominationInterest | undefined,
): NominatePlaceMatch {
  return {
    kind: "nomination",
    nominationId: nomination.id,
    googlePlaceId: nomination.googlePlaceId,
    name: nomination.name ?? "Unnamed business",
    location: formatNominateLocation(nomination.canonicalAddress),
    nominatedByName: nomination.nominatedByDisplayName,
    totalInterested: nomination.totalInterested,
    totalInterestDollars: toNumber(nomination.totalInterestDollars),
    heroCdnPath: nomination.heroCdnPath,
    userInterestDollars: myInterest?.intendedInvestmentDollars
      ? toNumber(myInterest.intendedInvestmentDollars)
      : undefined,
    userReason: myInterest?.reason ?? undefined,
    userFirstName: myInterest?.firstName ?? undefined,
    userLastName: myInterest?.lastName ?? undefined,
    userConsentToDisplay: myInterest?.consentToDisplay ?? undefined,
  };
}

function cardToMatch(
  card: CampaignBrowseShareableCard,
  myInterest: MyBusinessCardInterest | undefined,
): NominatePlaceMatch | null {
  if (!card.googlePlaceId) return null;
  // Every card in the browse feed is `browsable`, so it counts as a collision.
  // Password-protected cards can't be pledged inline (the amount is gated behind
  // the access code), so they route to a view-only "their page" link instead of
  // the pledge flow.
  if (card.requiresAccessCode) {
    return {
      kind: "onPlatformViewOnly",
      cardId: card.cardId.toString(),
      name: card.brandFriendlyName,
    };
  }
  return {
    kind: "onPlatform",
    cardId: card.cardId.toString(),
    googlePlaceId: card.googlePlaceId,
    name: card.brandFriendlyName,
    location: formatNominateLocation(card.canonicalAddress),
    totalInterested: card.totalInterested,
    totalInterestDollars: card.totalInterestDollars,
    heroCdnPath: card.heroCdnPath,
    userInterest: myInterest
      ? {
          interestDollarAmount: toNumber(myInterest.interestDollarAmount),
          firstName: myInterest.firstName ?? null,
          lastName: myInterest.lastName ?? null,
          supportiveMessage: myInterest.supportiveMessage ?? null,
          consentToDisplay: myInterest.consentToDisplay,
        }
      : undefined,
  };
}

export function useNominatePlaceLookup() {
  const nominationsQuery = useQuery(tableQueryOptions(tables.campaignBrowseNominations));
  const shareableCardsQuery = useQuery(tableQueryOptions(tables.campaignBrowseShareableCards));
  const myNominationInterestsQuery = useQuery(tableQueryOptions(tables.myNominationInterests));
  const myBusinessCardInterestsQuery = useQuery(tableQueryOptions(tables.myBusinessCardInterests));

  const nominations = (nominationsQuery.data ?? []) as CampaignBrowseNomination[];
  const shareableCards = (shareableCardsQuery.data ?? []) as CampaignBrowseShareableCard[];
  const myNominationInterests = (myNominationInterestsQuery.data ?? []) as MyNominationInterest[];
  const myBusinessCardInterests = (myBusinessCardInterestsQuery.data ??
    []) as MyBusinessCardInterest[];

  const myNominationInterestsByBusinessId = useMemo(() => {
    const map = new Map<string, MyNominationInterest>();
    for (const interest of myNominationInterests) {
      map.set(interest.nominatedBusinessId, interest);
    }
    return map;
  }, [myNominationInterests]);

  const myBusinessCardInterestsByCardId = useMemo(() => {
    const map = new Map<string, MyBusinessCardInterest>();
    for (const interest of myBusinessCardInterests) {
      map.set(interest.cardId.toString(), interest);
    }
    return map;
  }, [myBusinessCardInterests]);

  const lookupByGooglePlaceId = useMemo(() => {
    return (placeId: string): NominatePlaceMatch | null => {
      const linkedCard = shareableCards.find(
        (card) => card.googlePlaceId === placeId && card.linkedNominatedBusinessId !== undefined,
      );
      if (linkedCard?.linkedNominatedBusinessId) {
        const nomination = nominations.find(
          (row) => row.id === linkedCard.linkedNominatedBusinessId,
        );
        if (nomination) {
          return nominationToMatch(
            nomination,
            myNominationInterestsByBusinessId.get(nomination.id),
          );
        }
      }

      const card = shareableCards.find((row) => row.googlePlaceId === placeId);
      if (card) {
        return cardToMatch(card, myBusinessCardInterestsByCardId.get(card.cardId.toString()));
      }

      const nomination = nominations.find((row) => row.googlePlaceId === placeId);
      if (nomination) {
        return nominationToMatch(nomination, myNominationInterestsByBusinessId.get(nomination.id));
      }

      return null;
    };
  }, [
    nominations,
    shareableCards,
    myBusinessCardInterestsByCardId,
    myNominationInterestsByBusinessId,
  ]);

  const lookupCardById = useMemo(() => {
    return (cardId: string): NominatePlaceMatch | null => {
      const card = shareableCards.find((row) => row.cardId.toString() === cardId);
      if (!card) return null;
      return cardToMatch(card, myBusinessCardInterestsByCardId.get(cardId));
    };
  }, [shareableCards, myBusinessCardInterestsByCardId]);

  const resolveCardMatchById = useMemo(() => {
    return (cardId: string): NominatePlaceMatch => {
      const match = lookupCardById(cardId);
      if (match) return match;

      const card = shareableCards.find((row) => row.cardId.toString() === cardId);
      return {
        kind: "onPlatformViewOnly",
        cardId,
        name: card?.brandFriendlyName ?? "This business",
      };
    };
  }, [lookupCardById, shareableCards]);

  const getPlaceAnnotation = useMemo(() => {
    return (placeId: string): string | undefined => {
      const match = lookupByGooglePlaceId(placeId);
      if (!match) return undefined;
      return match.kind === "nomination" ? "Already nominated" : "On WITHUS";
    };
  }, [lookupByGooglePlaceId]);

  return {
    lookupByGooglePlaceId,
    lookupCardById,
    resolveCardMatchById,
    getPlaceAnnotation,
    isLoading:
      nominationsQuery.isLoading ||
      shareableCardsQuery.isLoading ||
      myNominationInterestsQuery.isLoading ||
      myBusinessCardInterestsQuery.isLoading,
  };
}
